import { AfterViewInit, Directive, ElementRef, NgZone, OnDestroy } from '@angular/core';
import { isBlankDetailValue } from './detail-section.util';

/**
 * Turns a blank value, or the literal words null/undefined, into the em dash
 * painted by `.tt-detail-value--blank`. Does not rewrite Angular text bindings.
 */
@Directive({
  selector: '.tt-detail-value, .tt-detail-value--emphasis',
})
export class DetailValueBlankDirective implements AfterViewInit, OnDestroy {
  private observer: MutationObserver | null = null;

  constructor(
    private readonly host: ElementRef<HTMLElement>,
    private readonly zone: NgZone,
  ) {}

  ngAfterViewInit(): void {
    this.sync();
    if (typeof MutationObserver === 'undefined') return;
    // Callbacks must not re-enter change detection. A value that rebuilds its
    // children every pass would otherwise queue another pass forever and lock the app.
    this.zone.runOutsideAngular(() => {
      this.observer = new MutationObserver(() => this.sync());
      this.observer.observe(this.host.nativeElement, {
        childList: true,
        characterData: true,
        subtree: true,
      });
    });
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }

  private sync(): void {
    const el = this.host.nativeElement;
    const hasElement = Array.from(el.childNodes).some((node) => node.nodeType === Node.ELEMENT_NODE);
    const blank = !hasElement && isBlankDetailValue(el.textContent);
    el.classList.toggle('tt-detail-value--blank', blank);
    if (blank) {
      el.setAttribute('aria-label', '—');
      el.dataset['detailBlank'] = 'true';
      return;
    }
    if (el.dataset['detailBlank']) {
      el.removeAttribute('aria-label');
      delete el.dataset['detailBlank'];
    }
  }
}
